// Texture Explorer — the full-library browser behind FillSection's
// "Browse all textures…" button. Portaled to document.body like the editor's
// other modals; stays open while the teacher tries textures (every thumb
// click applies live through apply(..., 'now') and the objProps mirror keeps
// the active state honest).
//
// Built for a 200+ item manifest: thumbs are <img loading="lazy"> (the
// browser defers offscreen fetches) and the grid uses ONE delegated
// click/hover handler pair instead of per-thumb closures.
//
// The preview strip is the sanctioned exception to mirrors-only: it reads
// editor.selectedObjects[0].text/fontFamily/fontWeight READ-ONLY so the
// teacher judges the hovered texture on their actual words.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { TbSlider } from '../../primitives/toolbarPrimitives';
import { TEXTURES, TEXTURE_CATEGORIES, textureById, textureUrl } from './fillPresets';
import { pushEscHandler, popEscHandler } from './StylesExplorerModal';

const PREVIEW_MAX_CHARS = 16;
const FALLBACK_SAMPLE = 'Aa Bb 123';

/** First line of the selected object's text, truncated for the preview strip. */
function previewSample(obj) {
  const first = String(obj?.text ?? '').split('\n')[0].trim();
  if (!first) return FALLBACK_SAMPLE;
  return first.length > PREVIEW_MAX_CHARS ? `${first.slice(0, PREVIEW_MAX_CHARS)}…` : first;
}

export default function TextureExplorerModal({
  objProps = {}, apply, commitNow, editor, onClose,
  // Pick mode (reused by the 3D panel to choose a material texture): the
  // grid returns an id via onPick instead of applying a text fill, active
  // state comes from pickActiveId, and the Pattern-size slider is hidden
  // (the 3D panel owns scale).
  pickMode = false, pickActiveId = null, onPick,
}) {
  const fv = objProps.textFill;
  const activeId = pickMode ? pickActiveId : (fv?.type === 'texture' ? fv.id : null);
  const texScale = fv?.type === 'texture' ? (fv.scale ?? 1) : 1;

  const [category, setCategory] = useState('all');
  const [query, setQuery] = useState('');
  const [hoverId, setHoverId] = useState(null);
  const [ground, setGround] = useState('light'); // preview backdrop tone

  // Esc must close ONLY this modal — and only when it's the TOP-most one.
  // The capture-phase listener lives in the shared Esc stack (see
  // StylesExplorerModal): this modal can open OVER the styles explorer, so
  // Esc pops modals one at a time, top first, and the editor's deselect
  // hotkey never sees the event. onClose rides a ref so the handler binds
  // once per mount.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const handler = () => closeRef.current?.();
    pushEscHandler(handler);
    return () => popEscHandler(handler);
  }, []);

  // A search term spans ALL categories and overrides the rail selection.
  const q = query.trim().toLowerCase();
  const shown = useMemo(() => {
    if (q) return TEXTURES.filter((t) => t.label.toLowerCase().includes(q));
    if (category === 'all') return TEXTURES;
    return TEXTURES.filter((t) => t.category === category);
  }, [q, category]);

  const sel = editor?.selectedObjects?.[0]; // sanctioned read-only peek
  const sample = previewSample(sel);

  const previewId = hoverId ?? activeId;
  const previewStyle = {
    fontFamily: sel?.fontFamily || 'Lexend, sans-serif',
    fontWeight: sel?.fontWeight || 700,
    ...(previewId
      ? {
        backgroundImage: `url(${textureUrl(previewId)})`,
        // Mirror the Pattern size setting so the strip previews the repeat
        // density too (240px at 100% reads like a few repeats per word).
        backgroundSize: `${Math.max(24, Math.round(240 * texScale))}px auto`,
        WebkitBackgroundClip: 'text',
        backgroundClip: 'text',
        color: 'transparent',
      }
      : { color: ground === 'light' ? '#0f172a' : '#f8fafc' }),
  };

  // Delegation: thumbs carry data-tex-id; the grid owns the only handlers.
  const texIdFrom = (e) => e.target.closest?.('[data-tex-id]')?.getAttribute('data-tex-id') ?? null;
  const handleGridClick = (e) => {
    const id = texIdFrom(e);
    if (!id) return;
    if (pickMode) { onPick?.(id); return; }
    apply(() => editor?.setTextFill({ type: 'texture', id, scale: texScale }), 'now');
  };
  const handleGridHover = (e) => {
    const id = texIdFrom(e);
    if (id) setHoverId(id);
  };

  const activeTex = activeId ? textureById(activeId) : null;
  const currentLabel = activeTex?.label ?? activeId; // legacy ids show raw

  return createPortal(
    <div
      className="cce-texmodal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="cce-texmodal" role="dialog" aria-modal="true" aria-label="Textures">
        <div className="cce-texmodal-head">
          <h2 className="cce-texmodal-title">Textures</h2>
          <input
            type="search"
            className="cce-texmodal-search"
            placeholder="Search all textures…"
            aria-label="Search textures"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setHoverId(null); }}
          />
          <button type="button" className="cce-texmodal-close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="cce-texmodal-main">
          <div className="cce-texmodal-rail">
            {[{ id: 'all', label: 'All' }, ...TEXTURE_CATEGORIES].map((c) => (
              <button
                key={c.id}
                type="button"
                data-cat={c.id}
                className={`cce-texmodal-cat${!q && category === c.id ? ' active' : ''}`}
                onClick={() => { setCategory(c.id); setQuery(''); setHoverId(null); }}
              >
                {c.label}
              </button>
            ))}
          </div>

          <div className="cce-texmodal-content">
            <div className="cce-texmodal-preview-wrap">
              <div className={`cce-texmodal-preview ${ground}`}>
                <span className="cce-texmodal-preview-text" style={previewStyle}>{sample}</span>
                <div className="cce-texmodal-grounds" role="group" aria-label="Preview background">
                  <button
                    type="button"
                    className={`cce-texmodal-ground light${ground === 'light' ? ' active' : ''}`}
                    title="Light background"
                    aria-label="Preview on a light background"
                    onClick={() => setGround('light')}
                  />
                  <button
                    type="button"
                    className={`cce-texmodal-ground dark${ground === 'dark' ? ' active' : ''}`}
                    title="Dark background"
                    aria-label="Preview on a dark background"
                    onClick={() => setGround('dark')}
                  />
                </div>
              </div>
            </div>

            <div
              className="cce-texmodal-grid"
              onClick={handleGridClick}
              onMouseOver={handleGridHover}
              onFocus={handleGridHover}
              onMouseLeave={() => setHoverId(null)}
            >
              {shown.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  data-tex-id={t.id}
                  className={`cce-texmodal-thumb${activeId === t.id ? ' active' : ''}`}
                  title={t.label}
                  aria-label={`Texture ${t.label}`}
                >
                  <img src={textureUrl(t.id)} alt="" loading="lazy" draggable={false} />
                  <span className="cce-texmodal-thumb-label">{t.label}</span>
                </button>
              ))}
              {shown.length === 0 && (
                <div className="cce-texmodal-empty">No textures match “{query.trim()}”.</div>
              )}
            </div>
          </div>
        </div>

        <div className="cce-texmodal-footer">
          <span className="cce-texmodal-current">
            {activeId
              ? <>Current texture: <strong>{currentLabel}</strong></>
              : 'No texture selected yet — click one to apply it.'}
          </span>
          {activeId && !pickMode && (
            <div className="cce-texmodal-scale">
              <TbSlider
                label={`Pattern size: ${Math.round(texScale * 100)}%`}
                value={Math.round(texScale * 100)}
                min={25}
                max={400}
                step={5}
                onChange={(v) => apply(() => editor?.setTextFill({ type: 'texture', id: activeId, scale: v / 100 }), 'defer')}
                onCommit={commitNow}
              />
            </div>
          )}
          <button
            type="button"
            className="cce-inspector-mini-btn primary cce-texmodal-done"
            onClick={onClose}
          >
            Done
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
